import { api } from './api';

export interface Vendor {
  id: string;
  name: string;
  code: string;
  email: string;
  phone: string;
  gstNumber?: string;
  panNumber?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  contactPerson?: string;
  contactPhone?: string;
  paymentTerms: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateVendorDto {
  name: string;
  code: string;
  email: string;
  phone: string;
  gstNumber?: string;
  panNumber?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  contactPerson?: string;
  contactPhone?: string;
  paymentTerms?: number;
}

export interface UpdateVendorDto extends Partial<CreateVendorDto> {}

export interface Product {
  id: string;
  name: string;
  sku: string;
  category: string;
  price: number;
  costPrice: number;
  unitPrice: number;
  currentStock: number;
}

export interface PurchaseOrderItem {
  id: string;
  productId: string;
  product: Product;
  quantity: number;
  // Accepted so far on goods receipts
  receivedQuantity?: number;
  unitPrice: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
}

export interface PurchaseOrder {
  id: string;
  plantId?: string | null;
  orderNumber: string;
  vendorId: string;
  vendor: Vendor;
  orderDate: Date;
  expectedDate: Date;
  status: 'draft' | 'approved' | 'partially_received' | 'received' | 'cancelled';
  // 0 as first approved; one more with every amendment
  revision?: number;
  approvedAt?: string | null;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  items: PurchaseOrderItem[];
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PurchaseOrderAmendment {
  id: string;
  revision: number;
  reason: string;
  createdAt: string;
  before: { grandTotal: number; items: { productId: string; quantity: number; unitPrice: number }[] };
  after: { grandTotal: number; items: { productId: string; quantity: number; unitPrice: number }[] };
}

export interface PurchaseOrderQueryParams {
  page: number;
  limit: number;
  search?: string;
  status?: PurchaseOrder['status'];
}

export interface PaginatedPurchaseOrders {
  data: PurchaseOrder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PurchaseOrderSummary {
  total: number;
  draft: number;
  approved: number;
  received: number;
  cancelled: number;
  totalValue: number;
}

export interface CreatePurchaseOrderItemDto {
  productId: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

export interface CreatePurchaseOrderDto {
  vendorId: string;
  plantId?: string;
  expectedDate: string; // YYYY-MM-DD
  items: CreatePurchaseOrderItemDto[];
  notes?: string;
}

class PurchaseService {
  // Vendor methods
  async getVendors(search?: string): Promise<Vendor[]> {
    const params = search ? { search } : {};
    const response = await api.get<any>('/vendors', { params });

    // The endpoint returns a paginated envelope; older builds returned a bare array
    if (Array.isArray(response.data?.data)) {
      return response.data.data;
    }
    return Array.isArray(response.data) ? response.data : [];
  }

  async getActiveVendors(): Promise<Vendor[]> {
    const response = await api.get<Vendor[]>('/vendors/active');
    return response.data;
  }

  async getVendor(id: string): Promise<Vendor> {
    const response = await api.get<Vendor>(`/vendors/${id}`);
    return response.data;
  }

  async createVendor(data: CreateVendorDto): Promise<Vendor> {
    const response = await api.post<Vendor>('/vendors', data);
    return response.data;
  }

  async updateVendor(id: string, data: UpdateVendorDto): Promise<Vendor> {
    const response = await api.patch<Vendor>(`/vendors/${id}`, data);
    return response.data;
  }

  async deleteVendor(id: string): Promise<void> {
    await api.delete(`/vendors/${id}`);
  }

  // Purchase Order methods
  async getPurchaseOrders(status?: string): Promise<PurchaseOrder[]> {
    const params = status ? { status } : {};
    const response = await api.get<PurchaseOrder[]>('/purchase', { params });
    return response.data;
  }

  async getPurchaseOrdersPage(query: PurchaseOrderQueryParams): Promise<PaginatedPurchaseOrders> {
    const params = {
      page: query.page,
      limit: query.limit,
      ...(query.search ? { search: query.search } : {}),
      ...(query.status ? { status: query.status } : {}),
    };
    const response = await api.get<PaginatedPurchaseOrders>('/purchase/paginated', { params });
    return response.data;
  }

  async getPurchaseOrderSummary(): Promise<PurchaseOrderSummary> {
    const response = await api.get<PurchaseOrderSummary>('/purchase/summary');
    return response.data;
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await api.get<PurchaseOrder>(`/purchase/${id}`);
    return response.data;
  }

  async createPurchaseOrder(data: CreatePurchaseOrderDto): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>('/purchase', data);
    return response.data;
  }

  async updatePurchaseOrder(id: string, data: Partial<CreatePurchaseOrderDto>): Promise<PurchaseOrder> {
    const response = await api.patch<PurchaseOrder>(`/purchase/${id}`, data);
    return response.data;
  }

  // Changes an approved order; the order as it was is kept with the reason
  async amendPurchaseOrder(id: string, data: Partial<CreatePurchaseOrderDto> & { reason: string }): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>(`/purchase/${id}/amend`, data);
    return response.data;
  }

  async getAmendments(id: string): Promise<PurchaseOrderAmendment[]> {
    const response = await api.get<PurchaseOrderAmendment[]>(`/purchase/${id}/amendments`);
    return response.data;
  }

  async deletePurchaseOrder(id: string): Promise<void> {
    await api.delete(`/purchase/${id}`);
  }

  async updatePurchaseOrderStatus(id: string, status: PurchaseOrder['status']): Promise<PurchaseOrder> {
    const response = await api.patch<PurchaseOrder>(`/purchase/${id}/status`, { status });
    return response.data;
  }
}

export const purchaseService = new PurchaseService();